import { BaseService } from "./BaseService";
import { OcrConversionInterface, OcrListResponseInterface, OcrProcessRequestInterface } from "../interfaces/OcrConversionInterface";

export class OcrService extends BaseService {
    private static instance: OcrService;

    private constructor() {
        super();
    }

    public static getInstance(): OcrService {
        if (!OcrService.instance) {
            OcrService.instance = new OcrService();
        }
        return OcrService.instance;
    }

    /**
     * Process a PDF or image file for OCR conversion
     * @param request The OCR process request
     * @returns Promise resolving to the conversion result
     */
    public async processOcr(request: OcrProcessRequestInterface): Promise<OcrConversionInterface> {
        await this.checkSessionValidity();

        const formData = new FormData();
        formData.append('sourceType', request.sourceType);

        if (request.file) {
            formData.append('file', request.file);
        }

        if (request.sourceUrl) {
            formData.append('sourceUrl', request.sourceUrl);
        }

        if (request.saveToS3 !== undefined) {
            formData.append('saveToS3', request.saveToS3.toString());
        }

        if (request.includeImages !== undefined) {
            formData.append('includeImages', request.includeImages.toString());
        }

        if (request.model) {
            formData.append('model', request.model);
        }

        const response = await this.axiosInstance.post(
            '/v2/ocr',
            formData,
            {
                headers: {
                    'Content-Type': 'multipart/form-data',
                }
            }
        );

        await this.checkResponseStatus(response);
        return response.data as OcrConversionInterface;
    }

    /**
     * List user's OCR conversions with pagination
     * @param page Page number (default: 1)
     * @param limit Items per page (default: 10)
     * @returns Promise resolving to paginated conversion list
     */
    public async listConversions(page: number = 1, limit: number = 10): Promise<OcrListResponseInterface> {
        await this.checkSessionValidity();

        const response = await this.axiosInstance.get(
            '/v2/ocr',
            {
                params: { page, limit },
                headers: this.getDefaultHeaders()
            }
        );

        await this.checkResponseStatus(response);
        return response.data as OcrListResponseInterface;
    }

    /**
     * Get a specific conversion by ID
     * @param id Conversion ID
     * @returns Promise resolving to the conversion
     */
    public async getConversion(id: number): Promise<OcrConversionInterface> {
        await this.checkSessionValidity();

        const response = await this.axiosInstance.get(
            `/v2/ocr/${id}`,
            { headers: this.getDefaultHeaders() }
        );

        await this.checkResponseStatus(response);
        return response.data as OcrConversionInterface;
    }

    /**
     * Delete a conversion
     * @param id Conversion ID
     * @returns Promise resolving when deletion is complete
     */
    public async deleteConversion(id: number): Promise<void> {
        await this.checkSessionValidity();

        const response = await this.axiosInstance.delete(
            `/v2/ocr/${id}`,
            { headers: this.getDefaultHeaders() }
        );

        await this.checkResponseStatus(response);
    }

    /**
     * Download the markdown result of a conversion
     * @param id Conversion ID
     * @returns Promise resolving to the markdown content
     */
    public async downloadMarkdown(id: number): Promise<string> {
        await this.checkSessionValidity();

        const response = await this.axiosInstance.get(
            `/v2/ocr/${id}/download`,
            {
                headers: {
                    ...this.getDefaultHeaders(),
                    'Accept': 'text/markdown'
                },
                responseType: 'text'
            }
        );

        await this.checkResponseStatus(response);
        return response.data as string;
    }
}
